import { useContext } from 'react';
import { Spacer } from '@nx-kit/layout';
import { useRouter } from 'next/router';
import useIsSticky from 'hooks/useIsSticky';
import { GUIDE_BASE, GUIDE_FALLBACK_LOCALE, isGuideOnlyLocale } from 'prismic/config';
import { generateLanguageOptions } from 'helpers/header.helper';
import { BreakpointContext } from 'contexts/breakpoint/BreakpointContext';
import Link from 'components/atoms/Link/Link';
import {
  LanguageSwitcherDesktop,
  LanguageSwitcherMobile,
} from 'components/molecules/LanguageSwitcher';
import Meta from 'components/organisms/Header/partials/desktop/Meta';
import { HeaderGuideProps } from './HeaderGuide.types';
import { Bar, StyledHeader, StyledLogo } from './HeaderGuide.styles';

// Localized "back to the museum" label for the guide meta bar.
const MUSEUM_LABEL: Record<string, string> = {
  de: 'Freilichtmuseum Ballenberg',
  fr: 'Musée en plein air Ballenberg',
  it: "Museo all'aperto Ballenberg",
  en: 'Ballenberg Open-Air Museum',
  default: 'Freilichtmuseum Ballenberg',
};

// Meta-bar items removed inside the guide (per Anja: drop Gutscheine + Blog).
const isHiddenInGuide = (linkText: string, url?: string | null): boolean => {
  const text = (linkText || '').toLowerCase();
  const href = (url || '').toLowerCase();
  const isBlog = text === 'blog' || href.includes('/blog');
  const isGutschein =
    href.includes('gutschein') ||
    ['gutschein', 'gift', 'voucher', 'bon cadeau', 'buoni'].some((needle) => text.includes(needle));
  return isBlog || isGutschein;
};

export const HeaderGuide: React.FC<HeaderGuideProps> = ({ alternateLanguages, meta_links }) => {
  const { locale } = useRouter();
  const { isSticky, isStickyProps } = useIsSticky();
  const bp = useContext(BreakpointContext);
  const isMobile = bp === 'xs' || bp === 'sm';
  // options
  const languageOptions = generateLanguageOptions(alternateLanguages);

  // `default` is the prefix-less German locale; map it to `de` for real URLs.
  // Guide-only locales (zh, ko, th) have no museum site or guide index, so link to English.
  const localePrefix = isGuideOnlyLocale(locale)
    ? GUIDE_FALLBACK_LOCALE
    : !locale || locale === 'default'
    ? 'de'
    : locale;
  const museumLabel = MUSEUM_LABEL[localePrefix] ?? MUSEUM_LABEL.default;

  // Guide meta bar: link back to the museum first, then the site meta links
  // without Gutscheine and Blog. Scoped to the guide; the main site bar is unchanged.
  const guideMetaLinks: HeaderGuideProps['meta_links'] = [
    {
      link: { link_type: 'Web', url: `/${localePrefix}` },
      link_text: museumLabel,
    } as HeaderGuideProps['meta_links'][number],
    ...meta_links.filter(({ link, link_text }) => !isHiddenInGuide(link_text, link?.url)),
  ];

  return (
    <>
      <div {...isStickyProps}></div>
      <StyledHeader>
        {!isMobile && <Meta links={guideMetaLinks} languageOptions={languageOptions} />}
        <Bar isSticky={isSticky}>
          <Link url={{ link_type: 'Web', url: `/${localePrefix}/${GUIDE_BASE}` }}>
            <StyledLogo />
          </Link>
          <Spacer />
          {isMobile ? (
            <LanguageSwitcherMobile withWhiteSkin withShortText options={languageOptions} />
          ) : (
            <LanguageSwitcherDesktop withWhiteSkin withShortText options={languageOptions} />
          )}
        </Bar>
      </StyledHeader>
    </>
  );
};
